import { useEffect, useState } from 'react'

import { Button, Modal, Trans } from '@/components'

import type { MemberTarget } from './AssignCreditDialog'
import { ConfirmReclaimDialog } from './ConfirmReclaimDialog'

type Props = {
  open: boolean
  target: MemberTarget | null
  onClose: () => void
  onConfirm: (payload: { memberId: string; reclaimAmount: number }) => Promise<void> | void
}

function formatCredits(n: number) {
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function ReclaimCreditDialog({ open, target, onClose, onConfirm }: Props) {
  const [amount, setAmount] = useState<string>('')
  const [showConfirm, setShowConfirm] = useState(false)

  useEffect(() => {
    if (open) {
      setAmount('')
      setShowConfirm(false)
    }
  }, [open])

  const memberBalanceCredits = (target?.currentCredit ?? 0) / 100

  const parsedAmount = Number(amount)
  const amountNum =
    Number.isNaN(parsedAmount) || parsedAmount < 0 ? 0 : Math.floor(parsedAmount * 1000) / 1000

  const afterCredits = memberBalanceCredits - amountNum
  const enoughMemberBalance = amountNum <= memberBalanceCredits
  const canSubmit = amountNum > 0 && target != null && enoughMemberBalance

  function sanitizeTo2dp(input: string) {
    let x = input.replace(/[^\d.]/g, '')

    const firstDot = x.indexOf('.')
    if (firstDot !== -1) {
      x = x.slice(0, firstDot + 1) + x.slice(firstDot + 1).replace(/\./g, '')
    }

    const [int = '', frac = ''] = x.split('.')
    const frac2 = frac.slice(0, 2)

    if (firstDot === -1) return int
    if (int === '' && frac2 === '') return ''
    return `${int || '0'}.${frac2}`
  }

  async function handleFinalConfirm() {
    if (!target) return
    await onConfirm({
      memberId: target.id,
      reclaimAmount: amountNum,
    })
    setShowConfirm(false)
  }

  return (
    <Modal open={open} onClose={onClose} className="w-[640px]">
      <Modal.Header>
        <div className="flex items-center gap-2">
          <Trans i18nKey="team.member.dialog.reclaimCreditFrom">Reclaim Credit from</Trans>{' '}
          <span className="text-primary">{target?.name}</span>
        </div>
      </Modal.Header>

      <Modal.Body>
        {/* Balance card */}
        <div className="rounded-2xl p-6 mb-4 bg-neutral-50 text-primary">
          <div className="t-lg mb-4">
            <Trans
              i18nKey="team.member.dialog.nameBalanceCredit"
              values={{ name: target?.name ?? '' }}
            >
              {`${target?.name ?? ''}`}&apos;s Balance (Credit)
            </Trans>
          </div>
          <div className="grid grid-cols-12">
            <div className="text-center col-span-5">
              <div className="h3">{formatCredits(memberBalanceCredits)}</div>
              <div className="mt-2 t-sb">
                <Trans i18nKey="team.member.dialog.current">Current</Trans>
              </div>
            </div>
            <div className="col-span-2 flex items-center justify-center py-4">
              <div className="w-px h-full bg-neutral-200"></div>
            </div>
            <div className="text-center col-span-5">
              <div className="h3">{formatCredits(afterCredits)}</div>
              <div className="mt-2 t-sb">
                <Trans i18nKey="team.member.dialog.after">After</Trans>
              </div>
            </div>
          </div>
        </div>

        {/* Reclaim Credit input */}
        <div className="mb-1">
          <div className="t-sb text-primary mb-2">
            <Trans i18nKey="team.member.dialog.reclaimCredit">Reclaim Credit</Trans>
          </div>
          <div className="relative">
            <input
              value={amount}
              onChange={(e) => setAmount(sanitizeTo2dp(e.target.value))}
              className="w-full h-11 px-4 pr-20 rounded-lg border border-neutral-200 outline-none focus:border-primary"
              placeholder="0"
              inputMode="decimal"
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 t-sb text-secondary">
              <Trans i18nKey="team.member.dialog.credit">Credit</Trans>
            </div>
          </div>
        </div>

        {!enoughMemberBalance && amountNum > 0 && (
          <div className="mt-3 t-sm text-error">
            <Trans i18nKey="team.member.dialog.exceedsMemberBalance">
              Amount exceeds member balance.
            </Trans>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button type="outline" className="min-w-[88px]" onClick={onClose}>
          <Trans i18nKey="common.cancel">Cancel</Trans>
        </Button>
        <Button
          type="danger"
          className="min-w-[88px]"
          disabled={!canSubmit}
          onClick={() => setShowConfirm(true)}
        >
          <Trans i18nKey="team.member.dialog.reclaim">Reclaim</Trans>
        </Button>
      </Modal.Footer>

      {target && (
        <ConfirmReclaimDialog
          open={showConfirm}
          target={target}
          reclaimAmount={amountNum}
          onClose={() => setShowConfirm(false)}
          onConfirm={handleFinalConfirm}
        />
      )}
    </Modal>
  )
}
